
import { extractAllBlocksFromTpl, blockRegistry, isTplAnnotationEnabled, FILTER_IDS } from "@okf/ootils";
import { _getRollupOptions } from "./_getRollupOptions";

/**
 * `filterKey` for an annotation option — the join to its equivalent filter, for
 * anything that needs to go from "this field" to "the filter for this field".
 *
 * Annotation filters are the one family that does NOT key on a generated key:
 * the hierarchy config expands each level with `filterKey: level.filterKey ||
 * level.filterId` (flattenFilterConfig → expandAnnoHierarchyConfig), so their
 * keys are the plain FILTER_IDS. A generateFilterKey() stamp here would look
 * right and match nothing.
 *
 * An option is identified by the collection it GROUPS BY — the terminal of a
 * rollup path, or the tag type itself. Anything outside the themes/subThemes/
 * tags hierarchy gets undefined rather than a guess, so callers fall back
 * instead of matching the wrong filter.
 */
const annoFilterKeyFor = (collection) => FILTER_IDS[collection];


export const _extractAllAnnoBasedDimensionsAsOptions = ({
  allTpls,
  isSelfManagedTenant,
  annotationTagsCount,
  smTagTypesConfig,
}) => {
  const tplsWithAnnotationsEnabled = allTpls.filter(isTplAnnotationEnabled);

  // get unique anno tags
  const allUniqueAnnoTags = Array.from(new Set(
    tplsWithAnnotationsEnabled
    .flatMap(t => blockRegistry.getAnnotationEnabledBlocks(extractAllBlocksFromTpl({ tpl: t })))
    .flatMap(block => blockRegistry.getTagTypesConfig(block, smTagTypesConfig))
    .map(conf => conf.tagType)
  ))

  
  let allVizFriendlyAnnoTagOptions = [
   ...allUniqueAnnoTags.map(d => ({
    display: allTpls.find(tpl => tpl.kp_content_type === d)?.general?.content?.title || d,
    // desc: 'Annotation Tag',
    value: `ANNO_TAG__tags.${d}`,
    valuePath: `annotations.tags.${d}`,
    isAnno: true,
    filterKey: annoFilterKeyFor(d),
   }))
  ]

  // roll em up
  let annoRollupOptions = allUniqueAnnoTags
    .flatMap(t => {
      const rollupPoss = _getRollupOptions({tagBlocksOnThisTpl: [{ props: { tagType: t}}], allTpls, isAnno: true, thisContentType: 'annotations-but-not-a-content-type'})
      
      return rollupPoss
    })

  const seenValues = new Set();
  
  const uniqueAnnoRollupOptions = annoRollupOptions.filter(option => {
    if (seenValues.has(option.value)) {
      return false;
    }
    seenValues.add(option.value);
    return true;
  });

  
  // Self-managed annotation rollups are COUNT-driven, not discovered from template blocks.
  // The annotation taxonomy (tags -> subThemes -> themes) lives on `collections`-segment
  // tpls, but isTplAnnotationEnabled only counts `publishing`-segment tpls — so block
  // discovery yields no anno rollups and uniqueAnnoRollupOptions is empty for these tenants.
  // (Previously we .find()'d the rollup in that empty list, got undefined, and crashed
  //  downstream at op.rollupType.) Build the options directly from annotationTagsCount,
  // mirroring ootils _self_managed_getFixedAnnoRollupBlocks. Shape matches the tagType
  // branch of _getRollupOptions so downstream (grouping, value namespacing, processrawconfig)
  // stays consistent with discovered rollups.
  const THIS_ANNO_CONTENT_TYPE = 'annotations-but-not-a-content-type'
  const buildSelfManagedAnnoRollupOption = (rollupPath) => {
    const finalItem = rollupPath[rollupPath.length - 1]
    const viaTitles = rollupPath
      .slice(0, -1)
      .map(item => allTpls.find(tpl => tpl.kp_content_type === item)?.general?.content?.title || item)
    return {
      rollupType: 'tagType',
      tagTypeCollectionToRollup: rollupPath[0],
      rollupPath,
      display: allTpls.find(tpl => tpl.kp_content_type === finalItem)?.general?.content?.title || finalItem,
      desc: `via ${viaTitles.join(' → ')}`,
      value: `${THIS_ANNO_CONTENT_TYPE}__VIA__${rollupPath.join('__')}`,
      isAnno: true,
      contentType: THIS_ANNO_CONTENT_TYPE,
      // Grouping tags by their subTheme IS the subThemes filter; by theme, the
      // themes filter. So the terminal of the path picks the hierarchy level.
      filterKey: annoFilterKeyFor(finalItem),
    }
  }

  let annoRollupOptionsToReturn = []
  if(isSelfManagedTenant){
    // subThemes rollup (group tags by parent subTheme) — needs subThemes to exist
    if(annotationTagsCount?.subThemes > 0){
      annoRollupOptionsToReturn.push(buildSelfManagedAnnoRollupOption(['tags', 'subThemes']))
    }

    // themes rollup — only meaningful when both subThemes and themes exist
    if(annotationTagsCount?.subThemes > 0 && annotationTagsCount?.themes > 0){
      annoRollupOptionsToReturn.push(buildSelfManagedAnnoRollupOption(['tags', 'subThemes', 'themes']))
    }

  }else{
    annoRollupOptionsToReturn = uniqueAnnoRollupOptions || []
  }

  return ({
    annoRollupOptions: annoRollupOptionsToReturn,
    annoTagOptions: allVizFriendlyAnnoTagOptions || [],
    options: [
    ...(allVizFriendlyAnnoTagOptions || []),
    ...annoRollupOptionsToReturn,

  ]})
}
